import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button, Chip, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem, Grid } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DashboardLayout from '../../common/DashboardLayout';
import AdminServices from '../AdminServices';

const DrawingEntries = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // First day of current month
    endDate: new Date() // Today
  });
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'custom'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate year options (current year ± 2 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchEntries = async () => {
    setLoading(true);
    setError('');

    try {
      let params = {};

      if (viewMode === 'monthly') {
        // For monthly view, calculate start and end dates for the selected month
        const startDate = new Date(selectedYear, selectedMonth - 1, 1);
        const endDate = new Date(selectedYear, selectedMonth, 0); // Last day of the month
        params = {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        };
      } else {
        params = {
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0]
        };
      }

      console.log('Fetching drawing entries with params:', params);
      console.log('Token from localStorage:', localStorage.getItem('token'));

      const response = await AdminServices.getDrawingEntries(params);
      console.log('API Response:', response);
      
      // Handle both array response and object response
      let entries = Array.isArray(response) ? response : (response?.entries || []);
      
      // Calculate summary data
      const uniqueEmails = new Set(entries.map(e => e.email)).size;
      const winners = entries.filter(e => e.isWinner).length;
      const entriesWithSchools = entries.filter(e => e.schoolName && e.schoolName.trim() !== '').length;
      
      // Group by day for daily stats
      const dailyStatsMap = {};
      entries.forEach(entry => {
        const day = entry.entryDay || entry.createdAt?.split('T')[0] || new Date(entry.createdAt).toISOString().split('T')[0];
        if (!dailyStatsMap[day]) {
          dailyStatsMap[day] = { date: day, totalEntries: 0, uniqueEntries: new Set() };
        }
        dailyStatsMap[day].totalEntries++;
        dailyStatsMap[day].uniqueEntries.add(entry.email);
      });
      
      const dailyStats = Object.values(dailyStatsMap).map(stat => ({
        ...stat,
        uniqueEntries: stat.uniqueEntries.size
      }));
      
      setData({
        entries: entries,
        summary: {
          totalEntries: entries.length,
          uniqueParticipants: uniqueEmails,
          winners: winners,
          entriesWithSchools: entriesWithSchools,
          dateRange: {
            startDate: params.startDate || new Date().toISOString().split('T')[0],
            endDate: params.endDate || new Date().toISOString().split('T')[0]
          }
        },
        dailyStats: dailyStats
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch drawing entries';
      setError(`API Error: ${errorMessage} (Status: ${err.response?.status || 'Unknown'})`);
      console.error('Error fetching drawing entries:', err);

      // Handle authentication errors
      if (err.response?.status === 401) {
        setError('Authentication required. Please log in as an admin to view drawing entries.');
      } else if (err.response?.status === 404) {
        setError('API endpoint not found (404). Backend may not be running or endpoint not implemented yet.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [viewMode, selectedMonth, selectedYear, dateRange]);

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleStartDateChange = (date) => {
    setDateRange(prev => ({ ...prev, startDate: date }));
  };

  const handleEndDateChange = (date) => {
    setDateRange(prev => ({ ...prev, endDate: date }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalEntries = () => {
    return data?.summary?.totalEntries || 0;
  };

  const getUniqueEmails = () => {
    return data?.summary?.uniqueParticipants || 0;
  };

  const getEntriesWithSchools = () => {
    // MonthlyDrawing model doesn't have schoolName field, so return 0 for now
    // This can be updated if schoolName is added to the model in the future
    return 0;
  };

  const getWinners = () => {
    return data?.summary?.winners || 0;
  };

  return (
    <DashboardLayout 
      role="admin" 
      layoutType="topnav"
      activeMenu="Drawing Entries"
      showBreadcrumbs={false}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Drawing Entries
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Options
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>View Mode</InputLabel>
                  <Select
                    value={viewMode}
                    onChange={handleViewModeChange}
                    label="View Mode"
                  >
                    <MenuItem value="monthly">Monthly View</MenuItem>
                    <MenuItem value="custom">Custom Date Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {viewMode === 'monthly' ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        label="Month"
                      >
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={selectedYear}
                        onChange={handleYearChange}
                        label="Year"
                      >
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={dateRange.startDate}
                      onChange={handleStartDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="End Date"
                      value={dateRange.endDate}
                      onChange={handleEndDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  onClick={fetchEntries}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Entries
                </Typography>
                <Typography variant="h4">
                  {getTotalEntries()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Unique Participants
                </Typography>
                <Typography variant="h4">
                  {getUniqueEmails()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Winners
                </Typography>
                <Typography variant="h4" color="success.main">
                  {getWinners()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  With School Info
                </Typography>
                <Typography variant="h4">
                  {getEntriesWithSchools()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Daily Statistics */}
        {data?.dailyStats && data.dailyStats.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Statistics
              </Typography>
              <Grid container spacing={2}>
                {data.dailyStats.map((day, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {new Date(day.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {day.totalEntries} total entries
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {day.uniqueEntries} unique participants
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Date Range Info */}
        {data?.summary?.dateRange && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Date Range
              </Typography>
              <Typography variant="body1">
                {new Date(data.summary.dateRange.startDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} to {new Date(data.summary.dateRange.endDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Entries List
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : !data?.entries || data.entries.length === 0 ? (
              <Typography variant="body1" color="textSecondary" align="center" sx={{ p: 3 }}>
                No entries found for the selected criteria.
              </Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Entry Day</TableCell>
                      <TableCell>Month</TableCell>
                      <TableCell>Entry Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.entries.map((entry, index) => (
                      <TableRow key={entry._id || entry.id || index}>
                        <TableCell>{entry.email}</TableCell>
                        <TableCell>{entry.entryDay || 'N/A'}</TableCell>
                        <TableCell>{entry.month || 'N/A'}</TableCell>
                        <TableCell>{formatDate(entry.entryDay || entry.createdAt)}</TableCell>
                        <TableCell>
                          {entry.isWinner ? (
                            <Chip
                              label="🏆 WINNER"
                              color="success"
                              size="small"
                              variant="filled"
                            />
                          ) : (
                            <Chip
                              label="Entry"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {entry.hasBeenNotified && entry.isWinner && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              (Notified)
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
        </Box>
      </LocalizationProvider>
    </DashboardLayout>
  );
};

export default DrawingEntries;
